import React from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Inventory as InventoryIcon,
  TrendingDown as LowIcon,
  RemoveShoppingCart as OutIcon,
  LocalShipping as ReceiveIcon,
  History as ActivityIcon,
  Savings as ValuationIcon,
  OpenInNew as NavigateIcon,
} from '@mui/icons-material';

// ── Movement badge colours — kept in sync with MovementTypeBadge.tsx ──────────

const MOVEMENT_TYPES = [
  { label: 'Receive',        color: '#1a7f37', bg: '#dafbe1', dir: '+',  note: 'Stock arrived from a supplier' },
  { label: 'Deplete',        color: '#6e40c9', bg: '#ede9fe', dir: '−',  note: 'Stock consumed during service' },
  { label: 'Transfer In',    color: '#0969da', bg: '#dbeafe', dir: '+',  note: 'Arrived from another location' },
  { label: 'Transfer Out',   color: '#9a6700', bg: '#fef9c3', dir: '−',  note: 'Sent to another location' },
  { label: 'Count Variance', color: '#cf222e', bg: '#ffebe9', dir: '±',  note: 'Difference found during a stock count' },
  { label: 'Adjustment',     color: '#6e7781', bg: '#f6f8fa', dir: '±',  note: 'Manual correction to stock' },
  { label: 'Waste',          color: '#bc4c00', bg: '#fff1e5', dir: '−',  note: 'Breakage, spoilage, or cork failure' },
  { label: 'Return',         color: '#8250df', bg: '#fbefff', dir: '−',  note: 'Bottles sent back to a supplier' },
];

// ── Sub-components ─────────────────────────────────────────────────────────────

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ icon, title, children }) => {
  const theme = useTheme();
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box sx={{ color: theme.palette.primary.main, display: 'flex', fontSize: 18 }}>
          {icon}
        </Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: '-0.01em' }}>
          {title}
        </Typography>
      </Box>
      {children}
    </Box>
  );
};

interface FieldRowProps {
  label: string;
  children: React.ReactNode;
  accent?: boolean;
}

const FieldRow: React.FC<FieldRowProps> = ({ label, children, accent }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        py: 0.75,
        px: 1.25,
        borderRadius: 1.5,
        '&:hover': { backgroundColor: theme.palette.action.hover },
      }}
    >
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{
          minWidth: 140,
          flexShrink: 0,
          color: accent ? theme.palette.primary.main : theme.palette.text.primary,
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
        {children}
      </Typography>
    </Box>
  );
};

interface NoteProps {
  children: React.ReactNode;
  variant?: 'info' | 'warning';
}

const Note: React.FC<NoteProps> = ({ children, variant = 'info' }) => {
  const theme = useTheme();
  const isWarning = variant === 'warning';
  return (
    <Box
      sx={{
        mt: 1.25,
        px: 1.75,
        py: 1.25,
        borderRadius: 2,
        backgroundColor: isWarning
          ? (theme.palette.mode === 'dark' ? 'rgba(250,173,20,0.1)' : '#fffbeb')
          : (theme.palette.mode === 'dark' ? 'rgba(9,105,218,0.1)' : '#f0f7ff'),
        borderLeft: `3px solid ${isWarning ? '#d97706' : theme.palette.primary.main}`,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
        {children}
      </Typography>
    </Box>
  );
};

// ── Stat card preview ──────────────────────────────────────────────────────────

interface StatPreviewProps {
  label: string;
  description: string;
  clickable?: boolean;
  alertColor?: string;
}

const StatPreview: React.FC<StatPreviewProps> = ({ label, description, clickable, alertColor }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        flex: '1 1 45%',
        minWidth: 160,
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        position: 'relative',
      }}
    >
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: alertColor ?? theme.palette.text.secondary,
          display: 'block',
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5, fontSize: '0.78rem' }}>
        {description}
      </Typography>
      {clickable && (
        <NavigateIcon
          sx={{ position: 'absolute', top: 10, right: 10, fontSize: 14, color: theme.palette.text.disabled }}
        />
      )}
    </Box>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
}

const InventoryHelpModal: React.FC<Props> = ({ open, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(6px)',
            backgroundColor: isDark ? 'rgba(0,0,0,0.55)' : 'rgba(30,30,50,0.35)',
          },
        },
      }}
      PaperProps={{
        sx: {
          background: isDark
            ? 'rgba(22, 22, 35, 0.90)'
            : 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(32px)',
          WebkitBackdropFilter: 'blur(32px)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}`,
          boxShadow: isDark
            ? '0 32px 80px rgba(0,0,0,0.6)'
            : '0 32px 80px rgba(0,0,0,0.16)',
          borderRadius: 3,
          maxHeight: '88vh',
        },
      }}
    >
      {/* ── Sticky header ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: isDark ? 'rgba(22,22,35,0.95)' : 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderRadius: '12px 12px 0 0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <InventoryIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2, letterSpacing: '-0.02em' }}>
              Inventory Dashboard Guide
            </Typography>
            <Typography variant="caption" color="text.secondary">
              How to read and navigate this page
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': { color: theme.palette.text.primary, backgroundColor: theme.palette.action.hover },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── Scrollable body ─────────────────────────────────────────────────── */}
      <DialogContent sx={{ px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 3.5 }}>

        {/* Intro */}
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          The Inventory Dashboard gives you a real-time snapshot of your venue's stock health —
          what's running low, what arrived recently, and what has been moving. Use it as your
          daily starting point before heading into the detail views.
        </Typography>

        <Divider />

        {/* ── Summary Cards ──────────────────────────────────────────────────── */}
        <Section icon={<InventoryIcon fontSize="inherit" />} title="Summary Cards">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
            The four cards at the top give you an at-a-glance health check for the whole venue.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 1.5 }}>
            <StatPreview
              label="Total Items"
              description="Count of active wine + location combinations being tracked. Not the total number of bottles."
            />
            <StatPreview
              label="Total Bottles"
              description="Sum of all positive stock quantities. Items at zero or below are not included."
            />
            <StatPreview
              label="Low Stock"
              description="Items above zero but at or below their reorder point. Clickable — opens the filtered inventory list."
              clickable
              alertColor="#9a6700"
            />
            <StatPreview
              label="Out of Stock"
              description="Items at zero or negative quantity. Clickable — opens the filtered inventory list."
              clickable
              alertColor="#cf222e"
            />
          </Box>
          <Note>
            <strong>Low Stock only counts items that have a reorder point set.</strong> If a wine shows
            no reorder point on its detail page, it will never appear in this count regardless of how
            few bottles remain. Open the inventory item and set a reorder point to start monitoring it.
          </Note>
        </Section>

        <Divider />

        {/* ── Received last 7 days ───────────────────────────────────────────── */}
        <Section icon={<ReceiveIcon fontSize="inherit" />} title="Received Last 7 Days">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            Shows the five most recent stock deliveries posted to your venue within the past seven days.
            Only movements of type <strong>Receive</strong> appear here — manual adjustments
            and other types are not counted.
          </Typography>
          <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
            <FieldRow label="Wine" accent>Full wine name including vintage, with bottle size and location shown below.</FieldRow>
            <FieldRow label="Qty">Bottles received, shown in green. Always a positive number.</FieldRow>
            <FieldRow label="Date">Relative time — "Today", "Yesterday", or how many days ago.</FieldRow>
          </Box>
          <Note>
            The number in the section header (e.g. <strong>Received last 7 days (14)</strong>) is the
            true 7-day total across all deliveries — not just the five shown. Click <strong>View all</strong> to
            open the full Movement Feed pre-filtered to Receive type.
          </Note>
        </Section>

        <Divider />

        {/* ── Recent Activity ────────────────────────────────────────────────── */}
        <Section icon={<ActivityIcon fontSize="inherit" />} title="Recent Activity">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            The twenty most recent movements across all types and all wines. This is the fastest
            way to see what has happened to stock across your whole venue without filtering.
          </Typography>
          <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
            <FieldRow label="Wine" accent>Full wine name including vintage, with bottle size shown below.</FieldRow>
            <FieldRow label="Type">A colour-coded badge showing what kind of movement it was. See the Movement Types section below.</FieldRow>
            <FieldRow label="Qty">Signed quantity change. Green with + is stock in; red with − is stock out.</FieldRow>
            <FieldRow label="Location">Storage location where the movement was recorded.</FieldRow>
            <FieldRow label="Date">Relative time since the movement was posted.</FieldRow>
          </Box>
        </Section>

        <Divider />

        {/* ── Movement Types Reference ───────────────────────────────────────── */}
        <Section icon={<ActivityIcon fontSize="inherit" />} title="Movement Types Reference">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
            Every movement carries one of these badges. The colour is consistent across the
            dashboard, the movement feed, and each individual wine's history.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {MOVEMENT_TYPES.map(mt => (
              <Box
                key={mt.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1.5,
                  '&:hover': { backgroundColor: theme.palette.action.hover },
                }}
              >
                <Chip
                  label={mt.label}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    color: mt.color,
                    backgroundColor: mt.bg,
                    border: 'none',
                    height: 22,
                    minWidth: 110,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    minWidth: 28,
                    fontWeight: 700,
                    fontFamily: 'monospace',
                    color: mt.dir === '+'
                      ? '#1a7f37'
                      : mt.dir === '−'
                        ? '#cf222e'
                        : theme.palette.text.secondary,
                    flexShrink: 0,
                  }}
                >
                  {mt.dir}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {mt.note}
                </Typography>
              </Box>
            ))}
          </Box>
        </Section>

        <Divider />

        {/* ── Navigating from here ───────────────────────────────────────────── */}
        <Section icon={<NavigateIcon fontSize="inherit" />} title="Where to Go From Here">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <FieldRow label="Low Stock card" accent>Opens the inventory list filtered to only low-stock items.</FieldRow>
            <FieldRow label="Out of Stock card" accent>Opens the inventory list filtered to only out-of-stock items.</FieldRow>
            <FieldRow label="Received → View all" accent>Opens the Movement Feed filtered to Receive movements.</FieldRow>
            <FieldRow label="Activity → View all" accent>Opens the full Movement Feed with no filter.</FieldRow>
            <FieldRow label="Stock estimate" accent>Opens the Estimated Stock Value page — see below.</FieldRow>
          </Box>
        </Section>

        <Divider />

        {/* ── Stock Estimate ─────────────────────────────────────────────────── */}
        <Section icon={<ValuationIcon fontSize="inherit" />} title="Stock Estimate (Estimated Stock Value)">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            Reached via the <strong>Stock estimate</strong> button in the top-right of this page.
            It calculates an indicative total value of your current stock as:
          </Typography>
          <Box
            sx={{
              textAlign: 'center',
              py: 1.5,
              mb: 1.5,
              borderRadius: 2,
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
              quantity × last recorded cost per item
            </Typography>
          </Box>
          <Note variant="warning">
            <strong>Indicative only — not accounting-grade.</strong> The value uses the last cost
            recorded for each wine, not a weighted average or FIFO calculation. Items with no cost
            history are excluded from the total and counted separately. Do not use this figure for
            financial reporting.
          </Note>
          <Box sx={{ mt: 1.5 }}>
            <FieldRow label="Items without cost" accent>Shown in amber in the summary. These are excluded from the total. Record costs when receiving stock to improve accuracy.</FieldRow>
            <FieldRow label="Location filter">Narrow the valuation to one storage location to see what's in a specific area.</FieldRow>
            <FieldRow label="As of [time]">The calculation runs fresh every time you load the page.</FieldRow>
          </Box>
        </Section>

        {/* ── Bottom spacer ──────────────────────────────────────────────────── */}
        <Box sx={{ height: 8 }} />

      </DialogContent>

      {/* ── Sticky footer ───────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'flex-end',
          background: isDark ? 'rgba(22,22,35,0.95)' : 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderRadius: '0 0 12px 12px',
        }}
      >
        <Typography
          variant="body2"
          color="text.disabled"
          sx={{ flex: 1, alignSelf: 'center', fontSize: '0.72rem' }}
        >
          Full documentation is available in the operational visibility manual.
        </Typography>
        <Box
          component="button"
          onClick={onClose}
          sx={{
            px: 3,
            py: 1,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: 'transparent',
            color: theme.palette.text.primary,
            fontWeight: 600,
            fontSize: '0.875rem',
            cursor: 'pointer',
            fontFamily: 'inherit',
            transition: 'all 0.15s',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
              borderColor: theme.palette.text.secondary,
            },
          }}
        >
          Close
        </Box>
      </Box>
    </Dialog>
  );
};

export default InventoryHelpModal;
